import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { TableBody } from '../components/ui/table';
import {
    History,
    Clock,
    Trophy,
    Target,
    MessageSquare,
    Users,
    Briefcase,
    DollarSign,
    FileText,
    ChevronRight,
    Calendar,
    TrendingUp,
    Eye,
    Filter,
    Search,
    BarChart3,
    CheckCircle,
    AlertCircle,
    User,
    Loader2
} from 'lucide-react';

const scenarioLabels = {
    candidate_screening: { label: 'Candidate Screening', icon: Users, color: 'bg-primary' },
    client_intake: { label: 'Client Intake', icon: Briefcase, color: 'bg-secondary' },
    objection_handling: { label: 'Objection Handling', icon: MessageSquare, color: 'bg-yellow-500' },
    salary_negotiation: { label: 'Salary Negotiation', icon: DollarSign, color: 'bg-emerald-500' },
    job_analysis: { label: 'Job Analysis', icon: FileText, color: 'bg-violet-500' }
};

const SimulationHistory = () => {
    const { api, isAdmin, isTrainer } = useAuth();
    const [loading, setLoading] = useState(true);
    const [history, setHistory] = useState([]);
    const [stats, setStats] = useState({});
    const [selectedSimulation, setSelectedSimulation] = useState(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [simulationDetail, setSimulationDetail] = useState(null);
    
    // Filters
    const [scenarioFilter, setScenarioFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    
    // Admin view
    const [viewMode, setViewMode] = useState('personal'); // 'personal' or 'all'
    const [adminHistory, setAdminHistory] = useState([]);
    const [adminStats, setAdminStats] = useState({});
    const [userStats, setUserStats] = useState([]);

    useEffect(() => {
        fetchHistory();
        if (isAdmin || isTrainer) {
            fetchAdminHistory();
            fetchUserStats();
        }
    }, []);

    const fetchHistory = async () => {
        try {
            const response = await api.get('/simulation/history');
            setHistory(response.data.history || []);
            setStats({
                total: response.data.total,
                averageScores: response.data.average_scores
            });
        } catch (error) {
            console.error('Failed to fetch history:', error);
            toast.error('Failed to load simulation history');
        } finally {
            setLoading(false);
        }
    };

    const fetchAdminHistory = async () => {
        try {
            const response = await api.get('/simulation/admin/history?limit=100');
            setAdminHistory(response.data.history || []);
            setAdminStats({
                total: response.data.total,
                averageOverallScore: response.data.average_overall_score,
                scenarioStats: response.data.scenario_stats
            });
        } catch (error) {
            console.error('Failed to fetch admin history:', error);
        }
    };

    const fetchUserStats = async () => {
        try {
            const response = await api.get('/simulation/admin/users-stats');
            setUserStats(response.data.users || []);
        } catch (error) {
            console.error('Failed to fetch user stats:', error);
        }
    };

    const fetchSimulationDetail = async (simId, isAdminView = false) => {
        setDetailLoading(true);
        try {
            const endpoint = isAdminView 
                ? `/simulation/admin/history/${simId}` 
                : `/simulation/history/${simId}`;
            const response = await api.get(endpoint);
            setSimulationDetail(response.data);
            setDetailDialogOpen(true);
        } catch (error) {
            console.error('Failed to fetch simulation detail:', error);
            toast.error('Failed to load simulation details');
        } finally {
            setDetailLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getScoreColor = (score) => {
        if (score >= 80) return 'text-emerald-500';
        if (score >= 60) return 'text-yellow-500';
        return 'text-red-500';
    };

    const filteredHistory = (viewMode === 'all' ? adminHistory : history).filter(sim => {
        if (scenarioFilter !== 'all' && sim.scenario_type !== scenarioFilter) return false;
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            return (
                sim.scenario_type?.toLowerCase().includes(query) ||
                sim.user_name?.toLowerCase().includes(query) ||
                sim.user_email?.toLowerCase().includes(query) ||
                sim.job_data?.job_title?.toLowerCase().includes(query)
            );
        }
        return true;
    });

    const renderSimulationCard = (sim, isAdminView = false) => {
        const scenario = scenarioLabels[sim.scenario_type] || { label: sim.scenario_type, icon: Target, color: 'bg-gray-500' };
        const ScenarioIcon = scenario.icon;
        
        return (
            <Card 
                key={sim.id} 
                className="cursor-pointer hover:border-primary/50 transition-all"
                onClick={() => fetchSimulationDetail(sim.id, isAdminView)}
            >
                <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${scenario.color}/10`}>
                                <ScenarioIcon className={`h-5 w-5 ${scenario.color.replace('bg-', 'text-')}`} />
                            </div>
                            <div>
                                <h4 className="font-medium">{scenario.label}</h4>
                                {sim.job_data?.job_title && (
                                    <p className="text-sm text-muted-foreground">{sim.job_data.job_title}</p>
                                )}
                                {isAdminView && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                        <User className="h-3 w-3 inline mr-1" />
                                        {sim.user_name} ({sim.user_email})
                                    </p>
                                )}
                                <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                    <span className="flex items-center gap-1">
                                        <Calendar className="h-3 w-3" />
                                        {formatDate(sim.started_at)}
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <MessageSquare className="h-3 w-3" />
                                        {sim.message_count} messages
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className={`text-2xl font-bold ${getScoreColor(sim.scores?.overall || 0)}`}>
                                {sim.scores?.overall || 0}
                            </div>
                            <p className="text-xs text-muted-foreground">Overall</p>
                            <Badge 
                                variant={sim.status === 'completed' ? 'default' : 'secondary'}
                                className="mt-2"
                            >
                                {sim.status || 'active'}
                            </Badge>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    };

    if (loading) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center h-[60vh]">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold flex items-center gap-3">
                            <History className="h-8 w-8 text-primary" />
                            Simulation History
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Review past simulations, scores, and detailed reports
                        </p>
                    </div>
                    
                    {(isAdmin || isTrainer) && (
                        <Tabs value={viewMode} onValueChange={setViewMode}>
                            <TabsList>
                                <TabsTrigger value="personal">My History</TabsTrigger>
                                <TabsTrigger value="all">All Users</TabsTrigger>
                            </TabsList>
                        </Tabs>
                    )}
                </div>

                {/* Stats Overview */}
                <div className="grid md:grid-cols-4 gap-4">
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-primary/10">
                                    <BarChart3 className="h-5 w-5 text-primary" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Total Simulations</p>
                                    <p className="text-2xl font-bold">
                                        {viewMode === 'all' ? adminStats.total : stats.total || 0}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-emerald-500/10">
                                    <Trophy className="h-5 w-5 text-emerald-500" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Average Score</p>
                                    <p className="text-2xl font-bold">
                                        {viewMode === 'all' 
                                            ? adminStats.averageOverallScore || 0
                                            : stats.averageScores?.overall || 0}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-blue-500/10">
                                    <Target className="h-5 w-5 text-blue-500" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Avg Communication</p>
                                    <p className="text-2xl font-bold">
                                        {stats.averageScores?.communication || 0}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-violet-500/10">
                                    <TrendingUp className="h-5 w-5 text-violet-500" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">
                                        {viewMode === 'all' ? 'Active Users' : 'Improvement'}
                                    </p>
                                    <p className="text-2xl font-bold">
                                        {viewMode === 'all' 
                                            ? userStats.filter(u => u.total_simulations > 0).length
                                            : '+12%'}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Filters */}
                <div className="flex items-center gap-4">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search simulations..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9"
                        />
                    </div>
                    <Select value={scenarioFilter} onValueChange={setScenarioFilter}>
                        <SelectTrigger className="w-[200px]">
                            <Filter className="h-4 w-4 mr-2" />
                            <SelectValue placeholder="Filter by scenario" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Scenarios</SelectItem>
                            <SelectItem value="candidate_screening">Candidate Screening</SelectItem>
                            <SelectItem value="client_intake">Client Intake</SelectItem>
                            <SelectItem value="objection_handling">Objection Handling</SelectItem>
                            <SelectItem value="salary_negotiation">Salary Negotiation</SelectItem>
                            <SelectItem value="job_analysis">Job Analysis</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* History List */}
                <div className="grid gap-4">
                    {filteredHistory.length > 0 ? (
                        filteredHistory.map(sim => renderSimulationCard(sim, viewMode === 'all'))
                    ) : (
                        <Card>
                            <CardContent className="py-12 text-center">
                                <History className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                                <h3 className="text-lg font-medium">No simulations found</h3>
                                <p className="text-muted-foreground">
                                    {searchQuery || scenarioFilter !== 'all' 
                                        ? 'Try adjusting your filters'
                                        : 'Start a simulation to see your history here'}
                                </p>
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* User Stats Table (Admin View) */}
                {viewMode === 'all' && userStats.length > 0 && (
                    <Card className="mt-6">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Users className="h-5 w-5" />
                                User Performance Overview
                            </CardTitle>
                            <CardDescription>
                                Simulation activity and scores by user
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b">
                                            <th className="text-left py-3 px-4 font-medium">User</th>
                                            <th className="text-center py-3 px-4 font-medium">Total</th>
                                            <th className="text-center py-3 px-4 font-medium">Completed</th>
                                            <th className="text-center py-3 px-4 font-medium">Avg Score</th>
                                            <th className="text-center py-3 px-4 font-medium">Last Activity</th>
                                        </tr>
                                    </thead>
                                    <TableBody>
                                        {userStats.slice(0, 10).map((user, idx) => (
                                            <tr key={user.user_id} className="border-b hover:bg-muted/50">
                                                <td className="py-3 px-4">
                                                    <div>
                                                        <p className="font-medium">{user.name}</p>
                                                        <p className="text-xs text-muted-foreground">{user.email}</p>
                                                    </div>
                                                </td>
                                                <td className="text-center py-3 px-4">{user.total_simulations}</td>
                                                <td className="text-center py-3 px-4">{user.completed_simulations}</td>
                                                <td className="text-center py-3 px-4">
                                                    <span className={`font-bold ${getScoreColor(user.average_score)}`}>
                                                        {user.average_score}
                                                    </span>
                                                </td>
                                                <td className="text-center py-3 px-4 text-sm text-muted-foreground">
                                                    {user.last_simulation ? formatDate(user.last_simulation) : 'N/A'}
                                                </td>
                                            </tr>
                                        ))}
                                    </TableBody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Detail Dialog */}
                <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Eye className="h-5 w-5" />
                                Simulation Details
                            </DialogTitle>
                            <DialogDescription>
                                {simulationDetail?.scenario_type && scenarioLabels[simulationDetail.scenario_type]?.label}
                                {simulationDetail?.job_data?.job_title && ` - ${simulationDetail.job_data.job_title}`}
                            </DialogDescription>
                        </DialogHeader>
                        
                        {detailLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            </div>
                        ) : simulationDetail && (
                            <div className="flex-1 overflow-y-auto space-y-6">
                                {/* User Info (Admin View) */}
                                {simulationDetail.user_info && (
                                    <div className="p-4 rounded-lg bg-muted/50 flex items-center gap-4">
                                        <div className="p-2 rounded-full bg-primary/10">
                                            <User className="h-5 w-5 text-primary" />
                                        </div>
                                        <div>
                                            <p className="font-medium">{simulationDetail.user_info.name}</p>
                                            <p className="text-sm text-muted-foreground">{simulationDetail.user_info.email}</p>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Scores */}
                                <div>
                                    <h4 className="font-medium mb-3 flex items-center gap-2">
                                        <Trophy className="h-4 w-4 text-primary" />
                                        Performance Scores
                                    </h4>
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                        {Object.entries(simulationDetail.scores || {})
                                            .filter(([key, value]) => typeof value === 'number' && key !== 'detailed_feedback')
                                            .map(([key, value]) => (
                                            <div key={key} className="p-3 rounded-lg bg-muted/50 text-center">
                                                <p className={`text-2xl font-bold ${getScoreColor(value)}`}>{value}</p>
                                                <p className="text-xs text-muted-foreground capitalize">
                                                    {key.replace(/_/g, ' ')}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Final Assessment */}
                                {simulationDetail.final_assessment && (
                                    <div>
                                        <h4 className="font-medium mb-3 flex items-center gap-2">
                                            <Target className="h-4 w-4 text-primary" />
                                            Final Assessment
                                        </h4>
                                        <div className="grid md:grid-cols-2 gap-4">
                                            <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                                                <h5 className="text-sm font-medium text-emerald-600 mb-2 flex items-center gap-1">
                                                    <CheckCircle className="h-4 w-4" />
                                                    Strengths
                                                </h5>
                                                <ul className="space-y-1">
                                                    {simulationDetail.final_assessment.strengths?.map((s, i) => (
                                                        <li key={i} className="text-sm text-muted-foreground">✓ {s}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/20">
                                                <h5 className="text-sm font-medium text-amber-600 mb-2 flex items-center gap-1">
                                                    <AlertCircle className="h-4 w-4" />
                                                    Areas for Improvement
                                                </h5>
                                                <ul className="space-y-1">
                                                    {simulationDetail.final_assessment.improvements?.map((s, i) => (
                                                        <li key={i} className="text-sm text-muted-foreground">→ {s}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                        {simulationDetail.final_assessment.overall_feedback && (
                                            <p className="mt-3 p-3 rounded-lg bg-muted/50 text-sm">
                                                {simulationDetail.final_assessment.overall_feedback}
                                            </p>
                                        )}
                                    </div>
                                )}
                                
                                {/* Conversation */}
                                <div>
                                    <h4 className="font-medium mb-3 flex items-center gap-2">
                                        <MessageSquare className="h-4 w-4 text-primary" />
                                        Conversation ({simulationDetail.messages?.length || 0} messages)
                                    </h4>
                                    <ScrollArea className="h-[300px] rounded-lg border p-4">
                                        <div className="space-y-4">
                                            {simulationDetail.messages?.map((msg, idx) => (
                                                <div 
                                                    key={idx} 
                                                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                                >
                                                    <div className={`max-w-[80%] p-3 rounded-lg ${
                                                        msg.role === 'user' 
                                                            ? 'bg-primary text-primary-foreground' 
                                                            : 'bg-muted'
                                                    }`}>
                                                        <p className="text-xs opacity-70 mb-1">
                                                            {msg.role === 'user' ? 'You' : 'AI'}
                                                        </p>
                                                        <p className="text-sm">{msg.content}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </ScrollArea>
                                </div>
                                
                                {/* Metadata */}
                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                    <span>Started: {formatDate(simulationDetail.started_at)}</span>
                                    {simulationDetail.completed_at && (
                                        <span>Completed: {formatDate(simulationDetail.completed_at)}</span>
                                    )}
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </MainLayout>
    );
};

export default SimulationHistory;
